import { displayPayslipTitle, localizeMonthLabel } from "@/utils/payroll-names";
import { useLocale } from "@/hooks/useLocale";
import { LopDetailNote } from "@/components/payslip/LopDetailNote";
import type { ILopDetail } from "@/types/(company)/payrollRuns.interface";
import { useTranslation } from "react-i18next";
import { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  Download,
  PenLine,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/utils/cn";
import { usePageTitle } from "@/hooks/usePageTitle";
import { withBranding } from "@/utils/helper";
import {
  useGetEmployeePayslipDetailQuery,
  useGetReceiptSignUrlQuery,
} from "@/services/(my-space)/employeePayrollApi";
import { useDownloadEmployeePayslip } from "@/services/(my-space)/employeePayslipDownload";
import { PayslipPreviewCard } from "@/components/payslip/PayslipPreviewCard";
import {
  RaiseDisputeDialog,
  type PayslipLineSeed,
} from "./_components/RaiseDisputeDialog";
import { useListMyDisputesQuery } from "@/services/(company)/payrollDisputesApi";

const toNumber = (v: string | number | null | undefined): number => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "string" ? Number(v.replace(/,/g, "")) : v;
  return Number.isFinite(n) ? n : 0;
};

const formatNumber = (n: number) =>
  n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const Section = ({
  title,
  children,
  bottomFlush,
}: {
  title: string;
  children: React.ReactNode;
  bottomFlush?: boolean;
}) => {
  const [open, setOpen] = useState(true);
  return (
    <div
      className={cn(
        "rounded-xs border border-borderSecondary bg-white overflow-hidden",
        bottomFlush && "rounded-b-none border-b-0",
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex items-center justify-between gap-3 w-full px-5 py-3",
          open && "border-b border-borderSecondary",
        )}
      >
        <span className="text-sm font-semibold text-gray-900">{title}</span>
        {open ? (
          <ChevronUp size={16} className="text-primary" />
        ) : (
          <ChevronDown size={16} className="text-gray-400" />
        )}
      </button>
      {open && <div className="px-5 py-4">{children}</div>}
    </div>
  );
};

const ReadOnlyLineRow = ({
  name,
  amount,
  currencyCode,
  tone,
  lopDetail,
}: {
  name: string;
  amount: number;
  currencyCode: string;
  tone: "earning" | "deduction";
  lopDetail?: ILopDetail | null;
}) => (
  <div
    className={cn(
      "flex items-center gap-3 rounded-xs px-3 py-2",
      tone === "earning" ? "bg-primary-light/20" : "bg-red-50",
    )}
  >
    <span
      className={cn(
        "size-2 rounded-full shrink-0",
        tone === "earning" ? "bg-yellow-500" : "bg-red-500",
      )}
    />
    <span className="flex flex-1 min-w-0 flex-col">
      <span className="text-sm font-medium text-gray-900 truncate">
        {name || "—"}
      </span>
      <LopDetailNote detail={lopDetail} />
    </span>
    <span className="text-[11px] text-gray-400">{currencyCode}</span>
    <span className="w-28 text-sm font-semibold text-end text-gray-900">
      {formatNumber(amount)}
    </span>
  </div>
);

const TotalsRow = ({
  label,
  currencyCode,
  amount,
  topFlush,
}: {
  label: string;
  currencyCode: string;
  amount: number;
  topFlush?: boolean;
}) => (
  <div
    className={cn(
      "flex items-center justify-between rounded-xs border border-borderSecondary bg-white px-5 py-3",
      topFlush && "rounded-t-none border-t border-t-borderSecondary mt-0",
    )}
  >
    <span className="text-sm font-semibold text-gray-700">{label}</span>
    <span className="text-sm font-bold text-gray-900">
      {currencyCode} {formatNumber(amount)}
    </span>
  </div>
);

const ReadOnlyField = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
      {label}
    </label>
    <div className="h-12 border border-input rounded-xs px-3 text-sm bg-gray-50 text-gray-700 flex items-center">
      {value || "—"}
    </div>
  </div>
);

interface MySpacePayslipDetailProps {
  payslipIdProp?: number;
  onBackInline?: () => void;
}

export const MySpacePayslipDetail = ({
  payslipIdProp,
  onBackInline,
}: MySpacePayslipDetailProps = {}) => {
  const { t } = useTranslation(["my-space", "company"]);
  const { formatDate: localeDate } = useLocale();
  usePageTitle(withBranding(t("page_titles.payslip")));
  const navigate = useNavigate();
  const { id } = useParams();
  const payslipId = payslipIdProp ?? Number(id);
  const skip = !payslipId || Number.isNaN(payslipId);
  const handleBack = onBackInline ?? (() => navigate(ROUTES.HOME.PAYROLL.ROOT));

  const { data: response, isLoading } = useGetEmployeePayslipDetailQuery(
    payslipId,
    { skip },
  );

  const detail = response?.data;
  const { download, isDownloading } = useDownloadEmployeePayslip();

  const receiptStatus = detail?.receiptSignatureStatus ?? null;
  const { data: signUrlResponse } = useGetReceiptSignUrlQuery(payslipId, {
    skip: skip || receiptStatus !== "pending",
  });
  const signUrl = signUrlResponse?.data?.url ?? null;

  const preview = detail?.payslipPreview;
  const currencyCode =
    preview?.currency?.code ?? detail?.netPay.currency.code ?? "";

  const [disputeOpen, setDisputeOpen] = useState(false);
  const disputeSeeds = useMemo<PayslipLineSeed[]>(() => {
    const earnings = (preview?.earnings ?? []).map((e: any) => ({
      lineId: e.id ?? null,
      lineType: "EARNING" as const,
      name: e.name ?? "",
      currentAmount: toNumber(e.amount),
    }));
    const benefits = (preview?.benefits ?? []).map((e: any) => ({
      lineId: e.id ?? null,
      lineType: "BENEFIT" as const,
      name: e.name ?? "",
      currentAmount: toNumber(e.amount),
    }));
    const deductions = (preview?.deductions ?? []).map((d: any) => ({
      lineId: d.id ?? null,
      lineType: "DEDUCTION" as const,
      name: d.name ?? "",
      currentAmount: toNumber(d.amount),
      lopDetail: d.lopDetail ?? null,
    }));
    return [...earnings, ...benefits, ...deductions];
  }, [preview]);
  const runOpenForDispute =
    detail?.runStatus === "APPROVED" ||
    detail?.runStatus === "PROCESSING" ||
    detail?.runStatus === "COMPLETED";

  const { data: disputesResp } = useListMyDisputesQuery(
    { payslipItemId: payslipId, limit: 50 },
    { skip: skip || !runOpenForDispute },
  );
  const disputeRows = disputesResp?.data ?? [];
  const openDispute = disputeRows.find((d) => d.status === "PENDING");
  const latestResolved = disputeRows.find(
    (d) => d.status === "RESOLVED" || d.status === "REJECTED",
  );
  const canDispute = runOpenForDispute && !openDispute;

  const totalEarnings = useMemo(
    () => (preview?.earnings ?? []).reduce((s, e) => s + toNumber(e.amount), 0),
    [preview],
  );
  const totalBenefits = useMemo(
    () => (preview?.benefits ?? []).reduce((s, e) => s + toNumber(e.amount), 0),
    [preview],
  );
  const totalDeductions = useMemo(
    () =>
      (preview?.deductions ?? []).reduce((s, d) => s + toNumber(d.amount), 0),
    [preview],
  );

  if (isLoading) {
    return (
      <div className="text-sm text-gray-400 py-12 text-center">
        {t("ui.loading_payslip")}
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="rounded-xs border border-borderSecondary bg-white py-12 px-6 flex flex-col items-center gap-3">
        <span className="text-sm font-semibold text-gray-700">
          {t("ui.payslip_not_found")}
        </span>
        <Button type="button" variant="outline" onClick={handleBack}>
          {t("ui.back_to_payroll")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 rounded-xs border border-borderSecondary bg-white px-5 py-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={handleBack}
            className="border border-card-background rounded-xs p-2 w-fit h-fit shrink-0"
          >
            <ChevronLeft className="text-primary size-5 rtl-flip" />
          </button>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold text-gray-900 truncate">
              {displayPayslipTitle(t, detail.title, localeDate)}
            </span>
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <span>{t("ui.home")}</span>
              <span>/</span>
              <span>{t("payroll.payroll")}</span>
              <span>/</span>
              <span className="text-gray-700 font-medium">
                {localizeMonthLabel(detail.header.periodLabel, localeDate)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {receiptStatus === "signed" && (
            <span className="inline-flex items-center gap-1 rounded-xs bg-success-100/10 text-success-100 px-2.5 py-1.5 text-xs font-semibold">
              <Check size={13} />
              {t("ui.receipt_confirmed")}
            </span>
          )}
          {receiptStatus === "pending" && signUrl && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => window.open(signUrl, "_blank")}
            >
              <PenLine size={14} />
              {t("ui.sign_to_confirm_receipt")}
            </Button>
          )}
          {runOpenForDispute && openDispute && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1 border-yellow-300 text-yellow-700 bg-yellow-50 cursor-pointer"
              onClick={() =>
                navigate(ROUTES.HOME.PAYROLL.DISPUTE_DETAIL(openDispute.id))
              }
            >
              <AlertCircle size={14} />
              {t("ui.dispute_pending")}
            </Button>
          )}
          {runOpenForDispute &&
            !openDispute &&
            latestResolved?.status === "RESOLVED" && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1 border-green-300 text-success-100 bg-success-100/10"
                onClick={() =>
                  navigate(
                    ROUTES.HOME.PAYROLL.DISPUTE_DETAIL(latestResolved.id),
                  )
                }
              >
                <Check size={14} />
                {t("ui.dispute_resolved")}
              </Button>
            )}
          {runOpenForDispute &&
            !openDispute &&
            latestResolved?.status === "REJECTED" && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1 border-red-300 text-red-600 bg-red-50"
                onClick={() =>
                  navigate(
                    ROUTES.HOME.PAYROLL.DISPUTE_DETAIL(latestResolved.id),
                  )
                }
              >
                <AlertCircle size={14} />
                {t("ui.dispute_rejected")}
              </Button>
            )}
          {canDispute && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1 border-orange-300 text-orange-600 hover:bg-orange-50"
              onClick={() => setDisputeOpen(true)}
            >
              <AlertCircle size={14} />
              {latestResolved ? t("ui.raise_again") : t("ui.raise_dispute")}
            </Button>
          )}
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="gap-1"
            disabled={!detail.payslipGeneratable || isDownloading}
            onClick={() => download(detail.id)}
          >
            <Download size={14} />
            {isDownloading ? t("ui.downloading") : t("ui.download_payslip")}
          </Button>
        </div>
      </div>

      <RaiseDisputeDialog
        open={disputeOpen}
        payslipId={detail.id}
        currencyCode={currencyCode}
        seedLines={disputeSeeds}
        onClose={() => setDisputeOpen(false)}
        onSuccess={() => navigate(ROUTES.HOME.PAYROLL.DISPUTES)}
      />

      {/* Two-column: form left, preview right */}
      <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-4 items-start">
        <div className="flex flex-col gap-4">
          {/* Payment Details */}
          <Section title={t("payroll.payment_details")}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ReadOnlyField
                label={t("payroll.payment_method")}
                value={preview?.paymentMode ?? "—"}
              />
              <ReadOnlyField
                label={t("payroll.currency")}
                value={currencyCode}
              />
              <ReadOnlyField
                label={t("payroll.bank_name")}
                value={preview?.employee.bankName ?? "—"}
              />
              <ReadOnlyField
                label={t("payroll.account_number")}
                value={preview?.employee.accountNumber ?? "—"}
              />
            </div>
          </Section>

          {/* Earnings */}
          <div className="flex flex-col">
            <Section title={t("payroll.earnings")} bottomFlush>
              <div className="flex flex-col gap-2">
                {(preview?.earnings ?? []).length === 0 ? (
                  <div className="text-xs text-gray-400 py-4 text-center">
                    {t("payroll.no_items")}
                  </div>
                ) : (
                  (preview?.earnings ?? []).map((e, idx) => (
                    <ReadOnlyLineRow
                      key={idx}
                      name={e.name}
                      amount={toNumber(e.amount)}
                      currencyCode={currencyCode}
                      tone="earning"
                    />
                  ))
                )}
              </div>
            </Section>
            <TotalsRow
              label={t("payroll.total_earnings")}
              currencyCode={currencyCode}
              amount={totalEarnings}
              topFlush
            />
          </div>

          {/* Benefits */}
          <div className="flex flex-col">
            <Section title={t("payroll.benefits")} bottomFlush>
              <div className="flex flex-col gap-2">
                {(preview?.benefits ?? []).length === 0 ? (
                  <div className="text-xs text-gray-400 py-4 text-center">
                    {t("payroll.no_items")}
                  </div>
                ) : (
                  (preview?.benefits ?? []).map((e, idx) => (
                    <ReadOnlyLineRow
                      key={idx}
                      name={e.name}
                      amount={toNumber(e.amount)}
                      currencyCode={currencyCode}
                      tone="earning"
                    />
                  ))
                )}
              </div>
            </Section>
            <TotalsRow
              label={t("labels.total_benefits")}
              currencyCode={currencyCode}
              amount={totalBenefits}
              topFlush
            />
          </div>

          {/* Deductions */}
          <div className="flex flex-col">
            <Section title={t("labels.deductions")} bottomFlush>
              <div className="flex flex-col gap-2">
                {(preview?.deductions ?? []).length === 0 ? (
                  <div className="text-xs text-gray-400 py-4 text-center">
                    {t("payroll.no_items")}
                  </div>
                ) : (
                  (preview?.deductions ?? []).map((d, idx) => (
                    <ReadOnlyLineRow
                      key={idx}
                      // The server names Loss of Pay in English (B45).
                      name={
                        d.lopDetail ? t("company:payrolls.loss_of_pay") : d.name
                      }
                      amount={toNumber(d.amount)}
                      currencyCode={currencyCode}
                      tone="deduction"
                      lopDetail={d.lopDetail}
                    />
                  ))
                )}
              </div>
            </Section>
            <TotalsRow
              label={t("labels.total_deductions")}
              currencyCode={currencyCode}
              amount={totalDeductions}
              topFlush
            />
          </div>

          {/* Net Pay */}
          <div className="flex items-center justify-between rounded-xs border border-borderSecondary bg-white px-5 py-4">
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-gray-700">
                {t("labels.net_pay")}
              </span>
              <span className="text-xs text-gray-400">
                {t("ui.after_all_deductions")}
              </span>
            </div>
            <span className="text-xl font-bold text-success-100">
              {currencyCode}{" "}
              {formatNumber(
                toNumber(preview?.netSalary ?? detail.netPay.amount),
              )}
            </span>
          </div>
        </div>

        {/* Payslip preview */}
        {preview && (
          <div className="sticky top-4">
            <PayslipPreviewCard preview={preview} />
          </div>
        )}
      </div>
    </div>
  );
};

export default MySpacePayslipDetail;
